import { useCallback, useMemo } from "react"

import { useGetAllCategories } from "@/utils/api/membership-service"
import type { MembershipCategoryResponse, RequirementDto } from "@/utils/api/membership-service/models"
import { isObjectLike, mapApiListResponse } from "@/utils/api/response-helpers"

import { resolveMembershipCategoryConfiguration } from "./utils"
import type {
  MembershipCategoryConfiguration,
  MembershipCategoryWithConfiguration,
} from "./types"
import { RequirementType, type Requirement, type MembershipCategory } from "./types"

export interface UseMembershipCategoryConfigurationsOptions {
  staleTime?: number
  enabled?: boolean
}

const requirementTypeValues = new Set<RequirementType>(Object.values(RequirementType))

function normaliseRequirementType(rawType: unknown): RequirementType {
  if (typeof rawType === 'string') {
    const upper = rawType.toUpperCase() as keyof typeof RequirementType
    if (upper in RequirementType) {
      return RequirementType[upper]
    }
    if (requirementTypeValues.has(rawType as RequirementType)) {
      return rawType as RequirementType
    }
  } else if (rawType && typeof rawType === 'object' && 'toString' in rawType) {
    const stringified = String(rawType)
    const upper = stringified.toUpperCase() as keyof typeof RequirementType
    if (upper in RequirementType) {
      return RequirementType[upper]
    }
    if (requirementTypeValues.has(stringified as RequirementType)) {
      return stringified as RequirementType
    }
  }

  return RequirementType.TEXT
}

function mapRequirementDtoToRequirement(requirement: unknown): Requirement | undefined {
  if (!isObjectLike(requirement)) {
    return undefined
  }

  const dto = requirement as RequirementDto
  const label = typeof dto.label === 'string' ? dto.label : ''
  if (!label) {
    return undefined
  }

  return {
    id: dto.id,
    label,
    mandatory: typeof dto.mandatory === 'boolean' ? dto.mandatory : false,
    type: normaliseRequirementType(dto.type),
    validationJson: dto.validationJson,
  }
}

function mapCategoryFromApi(category: unknown): MembershipCategory {
  if (!isObjectLike(category)) {
    return {} as MembershipCategory
  }

  const payload = category as MembershipCategoryResponse
  const requirements = Array.isArray(payload.requirements)
    ? payload.requirements
        .map((requirement) => mapRequirementDtoToRequirement(requirement))
        .filter((requirement): requirement is Requirement => Boolean(requirement))
    : undefined

  return {
    id: typeof payload.id === 'number' ? payload.id : undefined,
    categoryName: typeof payload.categoryName === 'string' ? payload.categoryName : undefined,
    description: typeof payload.description === 'string' ? payload.description : undefined,
    active: typeof payload.active === 'boolean' ? payload.active : undefined,
    annualFee: typeof payload.annualFee === 'number' ? payload.annualFee : undefined,
    targetAudience: typeof payload.targetAudience === 'string' ? payload.targetAudience : undefined,
    requirements,
  }
}

function mapCategoriesResponse(payload: unknown): MembershipCategory[] {
  return mapApiListResponse(payload, mapCategoryFromApi)
}

export function useMembershipCategoryConfigurations(
  options?: UseMembershipCategoryConfigurationsOptions,
) {
  const { staleTime = 5 * 60 * 1000, enabled = true } = options ?? {}

  const categoriesQuery = useGetAllCategories<MembershipCategory[]>({
    query: {
      staleTime,
      enabled,
      refetchOnWindowFocus: false,
      select: (response) => mapCategoriesResponse(response?.data),
    },
  })

  const categories = categoriesQuery.data ?? []

  const categoriesWithConfiguration: MembershipCategoryWithConfiguration[] = useMemo(
    () =>
      categories.map((category) => ({
        category,
        configuration: resolveMembershipCategoryConfiguration(category),
      })),
    [categories],
  )

  const configurationById = useMemo(() => {
    const map = new Map<number, MembershipCategoryConfiguration>()
    for (const entry of categoriesWithConfiguration) {
      const identifier = entry.category.id
      if (typeof identifier === "number") {
        map.set(identifier, entry.configuration)
      }
    }
    return map
  }, [categoriesWithConfiguration])

  const getConfigurationForCategoryId = useCallback(
    (categoryId?: number | null) => {
      if (categoryId != null) {
        const configuration = configurationById.get(categoryId)
        if (configuration) {
          return configuration
        }
      }
      return resolveMembershipCategoryConfiguration()
    },
    [configurationById],
  )

  return {
    ...categoriesQuery,
    categories,
    categoriesWithConfiguration,
    configurationById,
    getConfigurationForCategoryId,
  }
}

export function getConfigurationForCategory(
  category: MembershipCategory | null | undefined,
): MembershipCategoryConfiguration {
  return resolveMembershipCategoryConfiguration(category)
}
